import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import { FaPrint, FaUserGraduate, FaInfoCircle } from 'react-icons/fa';
import './StudentSummaryPrintable.css';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentSummaryPrintable = () => {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStudentSummary = async () => {
      const token = getAuthToken();
      if (!token) {
        setError('Unauthorized! Please log in.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await axios.post(
          `${appConstants.API_ROOT_URL}get_student_summary`,
          { id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data?.status === 'success') {
          const studentData = response.data.data || response.data.student;
          setStudent(studentData);
        } else {
          setError(response.data?.message || 'Failed to fetch student summary.');
        }
      } catch (err) {
        console.error('Error fetching student summary printable data:', err);
        setError(
          err.response?.data?.message ||
            'An error occurred while loading the student report.',
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStudentSummary();
    }
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  // Helper to render Attendance Month Calendar with fixed-width columns
  const renderAttendanceCalendar = (monthKey, data) => {
    if (!data || !Array.isArray(data.days) || data.days.length === 0) {
      return null;
    }

    const dateMap = {};
    data.days.forEach((day) => {
      if (day.date) {
        dateMap[day.date] = day.status;
      }
    });

    const firstDate = new Date(data.days[0].date);
    const year = firstDate.getFullYear();
    const month = firstDate.getMonth();

    const totalDays = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();

    const weeks = [[]];
    let currentWeek = weeks[0];

    // Empty cells before start of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(
        <td
          key={`pre-empty-${i}`}
          style={{ background: '#f8fafc', color: 'transparent' }}
        >
          &nbsp;
        </td>,
      );
    }

    // Days of month
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(
        d,
      ).padStart(2, '0')}`;
      const status = dateMap[dateStr] || 'noclass';

      let cellClass = 'cal-day-noclass';
      if (status === 'present') {
        cellClass = 'cal-day-present';
      } else if (status === 'absent') {
        cellClass = 'cal-day-absent';
      }

      currentWeek.push(
        <td key={dateStr} className={cellClass} title={`${dateStr}: ${status}`}>
          {d}
        </td>,
      );

      if (currentWeek.length === 7) {
        const newWeek = [];
        weeks.push(newWeek);
        currentWeek = newWeek;
      }
    }

    // Empty cells at end of month
    while (currentWeek.length > 0 && currentWeek.length < 7) {
      currentWeek.push(
        <td
          key={`post-empty-${currentWeek.length}`}
          style={{ background: '#f8fafc', color: 'transparent' }}
        >
          &nbsp;
        </td>,
      );
    }

    // Format display title (e.g. "2026-June" -> "June 2026")
    let displayMonthTitle = data.month || monthKey;
    if (displayMonthTitle.includes('-')) {
      const [yr, mName] = displayMonthTitle.split('-');
      displayMonthTitle = `${mName} ${yr}`;
    }

    return (
      <div className="calendar-card" key={monthKey}>
        <div className="calendar-card-header">
          <div className="calendar-card-title">{displayMonthTitle}</div>
          <div className="calendar-card-subtitle">
            P: <strong>{data.present || 0}</strong> | A:{' '}
            <strong>{data.absent || 0}</strong> | Total:{' '}
            <strong>{data.total || 0}</strong>
          </div>
        </div>

        <table className="calendar-table">
          <colgroup>
            <col style={{ width: '14.2857%' }} />
            <col style={{ width: '14.2857%' }} />
            <col style={{ width: '14.2857%' }} />
            <col style={{ width: '14.2857%' }} />
            <col style={{ width: '14.2857%' }} />
            <col style={{ width: '14.2857%' }} />
            <col style={{ width: '14.2857%' }} />
          </colgroup>
          <thead>
            <tr>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dayChar, i) => (
                <th key={i}>{dayChar}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, wIdx) => (
              <tr key={wIdx}>{week}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className="printable-page-wrapper"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        <div style={{ textAlign: 'center', color: '#475569' }}>
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: '3rem', height: '3rem' }}
          ></div>
          <p style={{ marginTop: '15px', fontWeight: 600 }}>
            Loading Student Progress Report...
          </p>
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="printable-page-wrapper">
        <div
          style={{
            maxWidth: '500px',
            margin: '40px auto',
            padding: '24px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
          }}
        >
          <FaInfoCircle
            style={{ fontSize: '2.5rem', color: '#ef4444', marginBottom: '12px' }}
          />
          <h5 style={{ color: '#1e293b' }}>Unable to Load Report</h5>
          <p style={{ color: '#64748b', fontSize: '13px' }}>
            {error || 'Student information could not be retrieved.'}
          </p>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => window.close()}
          >
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  const currentDateStr = new Date().toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const latestTestMarks = (student.test_marks || []).slice(0, 10);

  return (
    <div className="printable-page-wrapper">
      {/* On-Screen Action Bar (Hidden during Print) */}
      <div className="screen-action-bar no-print">
        <div>
          <span style={{ fontWeight: 700, color: '#0f172a' }}>
            {student.name}
          </span>
          <span style={{ color: '#64748b', marginLeft: '8px', fontSize: '12px' }}>
            (Ad. No: {student.admission_no || 'N/A'})
          </span>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={handlePrint}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontWeight: 600,
              padding: '6px 16px',
            }}
          >
            <FaPrint style={{ marginRight: '6px' }} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Printable Report Document (A4) */}
      <div id="printable-container">
        {/* Top Content Block */}
        <div className="report-body-wrapper">
          {/* Header */}
          <header className="report-header">
            <div>
              <h1 className="inst-title">ILM CAMPUS - POONOOR</h1>
              <div className="sub-title">Student Progress & Academic Report</div>
            </div>
            <div className="meta-info">
              <div>
                Date: <strong>{currentDateStr}</strong>
              </div>
              <div>
                Status:{' '}
                <strong
                  style={{
                    color: student.status === 'Active' ? '#16a34a' : '#dc2626',
                  }}
                >
                  {student.status || 'Active'}
                </strong>
              </div>
            </div>
          </header>

          {/* Top Section: Basic Info, Photo, Fee Summary */}
          <section
            className="section-break-inside-avoid"
            style={{ marginBottom: '6px' }}
          >
            <div className="section-title">Student Profile & Fee Overview</div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              {/* Column 1: Info Grid */}
              <div style={{ flex: '1.2', minWidth: '0' }}>
                <div className="info-grid">
                  <span className="info-label">Name:</span>
                  <span className="info-value" style={{ fontSize: '11px' }}>
                    {student.name}
                  </span>

                  <span className="info-label">Admission No:</span>
                  <span className="info-value">
                    {student.admission_no || 'N/A'}
                  </span>

                  <span className="info-label">Class / Batches:</span>
                  <span className="info-value">
                    {student.class_name ||
                      student.batches?.map((b) => b.batch_detail).join(', ') ||
                      'N/A'}
                  </span>

                  <span className="info-label">Date of Birth:</span>
                  <span className="info-value">{student.dob || 'N/A'}</span>

                  <span className="info-label">Gender:</span>
                  <span className="info-value">{student.gender || 'N/A'}</span>

                  <span className="info-label">Parent / Guardian:</span>
                  <span className="info-value">
                    {student.parent_name || 'N/A'}
                  </span>

                  <span className="info-label">Contact Phone:</span>
                  <span className="info-value">{student.phone || 'N/A'}</span>

                  <span className="info-label">Address:</span>
                  <span
                    className="info-value"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {student.address || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Column 2: Photo */}
              <div className="photo-box">
                {student.photo ? (
                  <img
                    src={`${appConstants.API_ROOT_URL}uploads/students/${student.photo}`}
                    alt={student.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML =
                        '<div style="color: #94a3b8; font-size: 9px; text-align: center;">No Photo</div>';
                    }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                    <FaUserGraduate style={{ fontSize: '1.8rem', opacity: 0.4 }} />
                    <div style={{ fontSize: '8px', marginTop: '3px' }}>
                      No Photo
                    </div>
                  </div>
                )}
              </div>

              {/* Column 3: Fee Summary */}
              <div style={{ width: '185px', minWidth: '180px', flexShrink: 0 }}>
                <div className="fee-summary-card">
                  <div className="fee-summary-header">
                    Fee Summary
                  </div>
                  <table className="fee-summary-table">
                    <tbody>
                      <tr>
                        <td className="fee-summary-label">
                          Total Course Fee
                        </td>
                        <td className="fee-summary-amount">
                          ₹{Number(student.fees?.total_fee ?? 0).toLocaleString()}
                        </td>
                      </tr>
                      {Boolean(student.fees?.total_discount) && (
                        <tr>
                          <td className="fee-summary-label">
                            Discount
                          </td>
                          <td
                            className="fee-summary-amount"
                            style={{ color: '#16a34a' }}
                          >
                            -₹{Number(student.fees?.total_discount).toLocaleString()}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="fee-summary-label">
                          Total Paid
                        </td>
                        <td
                          className="fee-summary-amount"
                          style={{ color: '#16a34a' }}
                        >
                          ₹{Number(student.fees?.total_paid ?? 0).toLocaleString()}
                        </td>
                      </tr>
                      <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                        <td
                          className="fee-summary-label"
                          style={{ color: '#0f172a', fontWeight: 700 }}
                        >
                          Balance Due
                        </td>
                        <td
                          className="fee-summary-amount"
                          style={{
                            color:
                              Number(student.fees?.balance ?? 0) > 0
                                ? '#dc2626'
                                : '#16a34a',
                            fontWeight: 700,
                            fontSize: '10.5px',
                          }}
                        >
                          ₹{Number(student.fees?.balance ?? 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Installments Mini Overview */}
                {student.fees?.installments &&
                  student.fees.installments.length > 0 && (
                    <div style={{ marginTop: '3px', fontSize: '8.5px' }}>
                      <div style={{ fontWeight: 600, color: '#475569' }}>
                        Installments:
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '2px',
                          marginTop: '1px',
                        }}
                      >
                        {student.fees.installments.map((inst, iIdx) => (
                          <span
                            key={iIdx}
                            style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: '3px',
                              padding: '1px 3px',
                              background:
                                inst.status === 'paid' ? '#dcfce7' : '#f8fafc',
                              color:
                                inst.status === 'paid' ? '#166534' : '#475569',
                            }}
                          >
                            {inst.title || `Inst ${inst.installment_no}`}: ₹
                            {inst.amount} ({inst.status})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </section>

          {/* Section 2: Attendance (3 Months Calendar) */}
          {student.attendance &&
            Object.keys(student.attendance).length > 0 && (
              <section
                className="section-break-inside-avoid"
                style={{ marginBottom: '6px' }}
              >
                <div className="section-title">
                  Attendance Record (Past 3 Months)
                </div>
                <div className="calendar-grid-container">
                  {Object.entries(student.attendance).map(([monthKey, monthData]) =>
                    renderAttendanceCalendar(monthKey, monthData),
                  )}
                </div>
                <div className="cal-legend">
                  <span className="cal-legend-title">
                    <strong>Legend:</strong>
                  </span>
                  <span className="cal-legend-item">
                    <span className="cal-legend-box cal-day-present"></span>
                    Present
                  </span>
                  <span className="cal-legend-item">
                    <span className="cal-legend-box cal-day-absent"></span>
                    Absent
                  </span>
                  <span className="cal-legend-item">
                    <span className="cal-legend-box cal-day-noclass">
                      <span style={{ textDecoration: 'line-through' }}>--</span>
                    </span>
                    No Class / Holiday
                  </span>
                </div>
              </section>
            )}

          {/* Section 3: Academic Performance - Exam Marks & Test Marks */}
          <section
            className="section-break-inside-avoid"
            style={{ marginBottom: '6px' }}
          >
            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
              }}
            >
              {/* Term Exams Table */}
              {student.exam_marks && student.exam_marks.length > 0 && (
                <div style={{ flex: '1.2' }}>
                  <div className="section-title">Term Exam Marks</div>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th
                          rowSpan="2"
                          style={{ width: '38%', verticalAlign: 'middle' }}
                        >
                          Subject
                        </th>
                        {student.exam_marks.map((exam, eIdx) => (
                          <th
                            key={eIdx}
                            colSpan="2"
                            style={{ textAlign: 'center' }}
                          >
                            {exam.term_name}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        {student.exam_marks.map((_, eIdx) => (
                          <React.Fragment key={eIdx}>
                            <th
                              style={{
                                textAlign: 'right',
                                fontSize: '8.5px',
                                width: '31%',
                              }}
                            >
                              Scored
                            </th>
                            <th
                              style={{
                                textAlign: 'right',
                                fontSize: '8.5px',
                                width: '31%',
                              }}
                            >
                              Max
                            </th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const subjectSet = new Set();
                        student.exam_marks.forEach((exam) => {
                          exam.marks?.forEach((m) => {
                            if (m.subject_name) subjectSet.add(m.subject_name);
                          });
                        });

                        const subjectList = Array.from(subjectSet);

                        if (subjectList.length === 0) {
                          return (
                            <tr>
                              <td
                                colSpan={1 + student.exam_marks.length * 2}
                                style={{
                                  textAlign: 'center',
                                  color: '#94a3b8',
                                  padding: '6px',
                                }}
                              >
                                No term exam records available
                              </td>
                            </tr>
                          );
                        }

                        return subjectList.map((subj, sIdx) => (
                          <tr key={sIdx}>
                            <td style={{ fontWeight: 600 }}>{subj}</td>
                            {student.exam_marks.map((exam, eIdx) => {
                              const markItem = exam.marks?.find(
                                (m) => m.subject_name === subj,
                              );
                              const val =
                                markItem?.mark_scored ||
                                markItem?.marks_obtained ||
                                '-';
                              const maxVal = markItem?.max_mark ?? '-';

                              return (
                                <React.Fragment key={eIdx}>
                                  <td
                                    style={{
                                      textAlign: 'right',
                                      fontWeight: val !== '-' ? 600 : 400,
                                    }}
                                  >
                                    {val}
                                  </td>
                                  <td
                                    style={{
                                      textAlign: 'right',
                                      color: '#64748b',
                                    }}
                                  >
                                    {maxVal}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        ));
                      })()}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                        <td>Total</td>
                        {student.exam_marks.map((exam, eIdx) => {
                          let totalScored = 0;
                          let totalMax = 0;
                          let hasNumeric = false;

                          exam.marks?.forEach((m) => {
                            const s = parseFloat(
                              m.mark_scored || m.marks_obtained,
                            );
                            if (!isNaN(s)) {
                              totalScored += s;
                              hasNumeric = true;
                            }
                            if (typeof m.max_mark === 'number') {
                              totalMax += m.max_mark;
                            }
                          });

                          return (
                            <React.Fragment key={eIdx}>
                              <td style={{ textAlign: 'right' }}>
                                {hasNumeric ? totalScored : '-'}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                {totalMax > 0 ? totalMax : '-'}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Test Marks Table (Latest 10) */}
              {latestTestMarks.length > 0 && (
                <div style={{ flex: '1' }}>
                  <div className="section-title">
                    Unit / Class Test Marks (Latest 10)
                  </div>
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th style={{ width: '32%' }}>Test</th>
                        <th style={{ width: '28%' }}>Subject</th>
                        <th style={{ textAlign: 'right', width: '20%' }}>Scored</th>
                        <th style={{ textAlign: 'right', width: '20%' }}>Max</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latestTestMarks.map((test, tIdx) => (
                        <tr key={tIdx}>
                          <td style={{ fontWeight: 600 }}>{test.test_name}</td>
                          <td>{test.subject_name}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>
                            {test.mark || '-'}
                          </td>
                          <td style={{ textAlign: 'right', color: '#64748b' }}>
                            {test.total_mark ?? '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* Section 4: Remarks & Observations */}
          <section
            className="section-break-inside-avoid remarks-section"
            style={{ marginBottom: '6px' }}
          >
            <div className="section-title">Teacher's Remarks & Conduct</div>
            <div className="remarks-box">
              {student.remarks || student.conduct ? (
                <div className="remarks-text">
                  {student.remarks || student.conduct}
                </div>
              ) : (
                <div className="remarks-lines">
                  <div className="remark-line"></div>
                  <div className="remark-line"></div>
                  <div className="remark-line"></div>
                  <div className="remark-line"></div>
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Section 5: Verification & Signatures (Bottom) */}
        <section className="section-break-inside-avoid signature-section">
          <div className="signature-row">
            <div className="signature-block">
              <div className="signature-line"></div>
              <div className="signature-title">Class Teacher</div>
            </div>

            <div className="signature-block">
              <div className="signature-line"></div>
              <div className="signature-title">Principal / Headmaster</div>
            </div>

            <div className="signature-block">
              <div className="signature-line"></div>
              <div className="signature-title">Parent / Guardian Signature</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StudentSummaryPrintable;
